import { useCallback, useState } from 'react';

import { formatDate } from '../utils.js';
import useBackendApi from '../hooks/useBackendApi';
import { RequestOptions } from '../types/httpRequests';
import { colors } from '../styles/fontStyles';
import {
  INCONCLUSIVE,
  NOT_DETECTED,
  DETECTED,
  ABOVE_RANGE,
  BELOW_RANGE,
  LOW,
  MEDIUM,
  HIGH,
  NOTE,
  DISCLAIMER,
  CONCENTRATION_UNITS,
  ASSUMED_ABOVE_RANGE_VALUE,
  NOT_DETECTED_STATUS,
  INCONCLUSIVE_STATUS,
  DETECTED_STATUS,
  BELOW_RANGE_STATUS,
  ABOVE_RANGE_STATUS,
  UNKNOWN_STATUS,
} from '../constants/report';
import {
  CompoundCardProps,
  CompoundMetaData,
  Data,
  DemographicData,
  DisplayDetailsForValue,
  FullCompoundMetaData,
  ReportFormatData,
} from '../types/reports';

const backendURL = process.env.REACT_APP_BACKEND_URL;

export const useLatestReport = () => {
  const { apiFetchRequest } = useBackendApi();
  const [latestReportLoading, setLatestReportLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLatestReport = useCallback(async () => {
    setLatestReportLoading(true);
    setError(null);

    try {
      const apiRequestOptions: RequestOptions = {
        endpoint: `${backendURL}/reports/`,
        headers: {},
      };
      const data = await apiFetchRequest(apiRequestOptions);

      if (data.status !== 200) {
        setLatestReportLoading(false);
        setError(data.body.statusText);
        return { latestReport: null, message: data.body.statusText };
      }

      let reports = data.body.reports;
      if (reports !== undefined && reports.length > 0) {
        const datedReports = reports.filter(
          (r: { report_date: string }) => r?.report_date && !Number.isNaN(Date.parse(r.report_date)),
        );

        let latestReport: any | null = null;

        if (datedReports.length > 0) {
          datedReports.sort(
            (a: { report_date: string }, b: { report_date: string }) =>
              Date.parse(b.report_date) - Date.parse(a.report_date),
          );
          latestReport = datedReports[0];
        } else {
          // Fallback
          latestReport = reports[reports.length - 1];
        }
        setLatestReportLoading(false);
        return { latestReport: latestReport, formattedDate: formatDate(latestReport.report_date), message: null };
      } else {
        setLatestReportLoading(false);
        return { latestReport: null, formattedDate: null, message: null };
      }
    } catch (error) {
      const errMsg = 'Error while fetching latest report.';
      setLatestReportLoading(false);
      setError(errMsg);
      return { latestReport: null, message: errMsg };
    }
  }, []);

  return {
    latestReportLoading,
    fetchLatestReport,
    error,
  };
};

export const getTestName = (name: string) => {
  const regex = /\(([^)]+)\)[^(]*$/;
  const match = name.match(regex);
  let short_name;

  if (match && match[1]) {
    short_name = match[1];
  } else {
    short_name = name;
  }
  return short_name;
};

export const getCompoundMetaForTest = (
  testId: number,
  compoundName: string,
  fullCompoundMetaData: FullCompoundMetaData,
): CompoundMetaData | undefined => {
  const testData = fullCompoundMetaData[testId.toString()];
  if (!testData) return undefined;

  return testData[compoundName];
};

export const getDisplayDetailsForValue = (
  value: string | null,
  reportFormatData: ReportFormatData,
  fullCompoundMetaData: FullCompoundMetaData,
  test_id: number,
): DisplayDetailsForValue => {
  const {
    testName,
    lowerBoundFemale: lowerReportableValue,
    upperBoundFemale: higherReportableValue,
    testUnit,
  } = reportFormatData;

  const testNameAbbr = getTestName(testName);
  const testNameOnly = testName.replace(`(${testNameAbbr})`, '').replace(/[()]/g, '').trim();

  const vF = parseFloat(value || '0');
  const lrvF = parseFloat(lowerReportableValue);
  const hrvF = parseFloat(higherReportableValue);

  const compound = getCompoundMetaForTest(test_id, testNameAbbr, fullCompoundMetaData);
  const lod = compound ? parseFloat(compound.lod) : 0;
  const loq = compound ? parseFloat(compound.loq) : 0;

  const baseDetails = {
    testNameAbbr,
    testNameOnly,
    testName,
    testUnit,
    valueAsFloat: vF,
    lowerReportableValue,
    higherReportableValue,
  };

  const createDisplayDetails = (
    label: string,
    displayValue: string,
    color: string,
    fillFontColor: string,
    detectionStatus: string,
    isDetected = false,
  ): DisplayDetailsForValue => ({
    ...baseDetails,
    label,
    value: displayValue,
    color,
    fillFontColor,
    detectionStatus,
    isDetected,
  });

  // no value, not detected
  if (value === null)
    return createDisplayDetails(NOT_DETECTED, 'ND', colors.green700, colors.blue900, NOT_DETECTED_STATUS);

  // Value of “Inconclusive”
  if (value.toLowerCase().trim().includes(INCONCLUSIVE.toLowerCase()))
    return createDisplayDetails(INCONCLUSIVE, '-', colors.gray300, colors.blue900, INCONCLUSIVE_STATUS);

  // Value of “Not Detected”
  if (value.toLowerCase().trim().replace(/\s+/g, ' ').includes(NOT_DETECTED.toLowerCase()))
    return createDisplayDetails(NOT_DETECTED, '-', colors.green700, colors.blue900, NOT_DETECTED_STATUS);

  // For Not Detected
  if (0 <= vF && vF < lod)
    return createDisplayDetails(NOT_DETECTED, '-', colors.green700, colors.blue900, NOT_DETECTED_STATUS);

  if (vF === 0)
    // Value of zero (since we are comparing the float, this should account for 0, 0.0 etc..)
    return createDisplayDetails(NOT_DETECTED, '-', colors.green700, colors.blue900, NOT_DETECTED_STATUS);

  // Less than reportable range
  if (lod <= vF && vF < loq)
    return createDisplayDetails(BELOW_RANGE, '-', colors.green700, colors.blue900, BELOW_RANGE_STATUS);

  // In reportable range
  if (lrvF <= vF && vF <= hrvF)
    return createDisplayDetails(DETECTED, value, colors.primaryBlue, colors.white, DETECTED_STATUS, true);

  // Out of reportable range
  if (hrvF < vF) return createDisplayDetails(ABOVE_RANGE, '-', colors.purple700, colors.white, ABOVE_RANGE_STATUS);

  // The following should never happen
  return createDisplayDetails('Unknown', '', colors.purple700, colors.white, UNKNOWN_STATUS);
};

export function calculateNasemScore(
  compounds: CompoundCardProps[],
  compoundMetaData: Record<string, CompoundMetaData>,
): { nasemScore: string; nasemLevel: string } {
  let totalNasemScore = 0;

  compounds.forEach((compound) => {
    const compoundLabel = compound.ddfv.label;
    const isDetected = compoundLabel === DETECTED;
    const isAboveRange = compoundLabel === ABOVE_RANGE;

    if (!isDetected && !isAboveRange) return;

    const compoundAbbreviation = compound.ddfv.testNameAbbr;
    const metaData = compoundMetaData[compoundAbbreviation];

    if (!metaData?.nasem_score) return;

    const compoundLOQ = parseFloat(metaData.loq);
    if (isNaN(compoundLOQ)) return;

    if (isAboveRange) {
      if (ASSUMED_ABOVE_RANGE_VALUE >= compoundLOQ) {
        totalNasemScore += ASSUMED_ABOVE_RANGE_VALUE * 2;
      }
      return;
    }

    if (compound.value === null) return;
    const compoundValue = parseFloat(compound.value ?? 'NaN');
    if (!isNaN(compoundValue) && compoundValue >= compoundLOQ) {
      totalNasemScore += compoundValue * 2;
    }
  });

  let nasemLevel = '';
  if (totalNasemScore < 2) {
    nasemLevel = LOW;
  } else if (totalNasemScore >= 2 && totalNasemScore <= 20) {
    nasemLevel = MEDIUM;
  } else {
    nasemLevel = HIGH;
  }

  const formattedNasemScore = totalNasemScore.toFixed(1);
  return { nasemScore: formattedNasemScore, nasemLevel };
}

export function calculateReportScores(
  compoundDataWithDdfv: CompoundCardProps[],
  compoundMetaData: Record<string, CompoundMetaData>,
): { totalCompoundsDetected: number; totalPFASScore: number; nasemScore: string; nasemLevel: string } {
  const totalCompoundsDetected = compoundDataWithDdfv.filter((item) => item.ddfv.label === DETECTED).length;

  const totalPFASScore = compoundDataWithDdfv
    .filter((item) => item.ddfv.label === DETECTED && item.value !== null && !isNaN(parseFloat(item.value)))
    .reduce((sum, item) => sum + parseFloat(item.value!), 0);

  const { nasemScore, nasemLevel } = calculateNasemScore(compoundDataWithDdfv, compoundMetaData);
  return { totalCompoundsDetected, totalPFASScore, nasemScore, nasemLevel };
}

export function getColorForNasemLevel(nasemLevel: string): string {
  switch (nasemLevel) {
    case LOW:
      return colors.greenDark;
    case MEDIUM:
      return colors.orangeDark;
    case HIGH:
      return colors.redDark;
    default:
      return colors.white;
  }
}

// getNasemScoreConfig() is used to get color configurations based on the NASEM score.
export const getNasemScoreConfig = (nasemScore: number, theme: any) => {
  if (nasemScore < 2) {
    return {
      exposureTextColor: theme.custom.report.details.reportNasemScoreBadge.low.exposureTextColor,
      badgeBackgroundColor: theme.custom.report.details.reportNasemScoreBadge.low.badgeBackgroundColor,
      badgeTextColor: theme.custom.report.details.reportNasemScoreBadge.low.badgeTextColor,
    };
  } else if (nasemScore >= 2 && nasemScore <= 20) {
    return {
      exposureTextColor: theme.custom.report.details.reportNasemScoreBadge.medium.exposureTextColor,
      badgeBackgroundColor: theme.custom.report.details.reportNasemScoreBadge.medium.badgeBackgroundColor,
      badgeTextColor: theme.custom.report.details.reportNasemScoreBadge.medium.badgeTextColor,
    };
  } else {
    return {
      exposureTextColor: theme.custom.report.details.reportNasemScoreBadge.high.exposureTextColor,
      badgeBackgroundColor: theme.custom.report.details.reportNasemScoreBadge.high.badgeBackgroundColor,
      badgeTextColor: theme.custom.report.details.reportNasemScoreBadge.high.badgeTextColor,
    };
  }
};

// getExposureText() provides an exposure message and color based on a percentile value.
export const getExposureText = (percentile: number) => {
  if (percentile < 40)
    return {
      exposureText: 'This means your exposure is below average.',
      exposureTextColor: colors.lightGreen,
      exposureLevel: 'low',
    };
  if (percentile >= 40 && percentile <= 50)
    return {
      exposureText: 'This means your exposure is about average.',
      exposureTextColor: colors.lightBrown,
      exposureLevel: 'medium',
    };
  return {
    exposureText: 'This means your exposure is above average.',
    exposureTextColor: colors.lightRed,
    exposureLevel: 'high',
  };
};

export const findPercentile = (value: any, data: any) => {
  // Find and return the largest percentile below or equal to the value
  let matchedPercentile = data[0].y;

  for (const point of data) {
    if (point.x <= value) {
      matchedPercentile = point.y;
    } else {
      break; // Stop loop
    }
  }
  return matchedPercentile;
};

export const getValueForPercentile = (percentile: any, data: any) => {
  // Find the value for the percentile and return the value
  let matchedValue = null;
  matchedValue = data[percentile].x;
  return matchedValue;
};

export const getSelectedData = (
  reportDemographicData: DemographicData | undefined,
  dataset: string,
  selectedFilter: string,
  selectedCompound?: string,
): Data | null => {
  if (!reportDemographicData) return null;
  let data = null;
  switch (dataset) {
    case 'NHANES':
      data = getNHANESData(reportDemographicData, selectedFilter, selectedCompound);
      break;
    case 'RH':
      data = getRHData(reportDemographicData);
      break;
  }

  return data
    ? Object.entries(data).map(([key, value]) => ({
        x: Number(value),
        y: Number(key),
      }))
    : null;
};

const getNHANESData = (
  reportDemographicData: DemographicData | undefined,
  selectedFilter: string,
  selectedCompound?: string,
) => {
  if (!reportDemographicData) return null;

  // Determine demographic dataset based on selectedFilter
  const demographicKey =
    selectedFilter === 'US Male'
      ? 'all_males_18_and_over'
      : selectedFilter === 'US Female'
        ? 'all_females_18_and_over'
        : 'all_18_and_over';

  const demographicData = reportDemographicData[demographicKey];
  if (!demographicData) return null;

  // If selectedCompound is provided, check if it exists in NHANES compound data
  if (selectedCompound) {
    // handle special case for certain compounds by replacing "-" with "_"
    selectedCompound = selectedCompound.replace(/-/g, '_');

    const compoundKey = Object.keys(demographicData?.compound_data || {}).find(
      (key) => key.toLowerCase() === selectedCompound?.toLowerCase(),
    );

    // If the compound exists, return its NHANES data
    if (compoundKey) {
      return demographicData.compound_data[compoundKey]?.all_score_percentiles || null;
    }
  }

  // If the compound is NOT found, return NHANES demographic
  return demographicData.nasem?.all_score_percentiles || null;
};

export const isCompoundInNHANES = (
  reportDemographicData: DemographicData | undefined,
  selectedFilter: string,
  selectedCompound: string,
): boolean => {
  if (!reportDemographicData || !selectedCompound) return false;

  // handle special case for certain compounds by replacing "-" with "_"
  selectedCompound = selectedCompound.replace(/-/g, '_');

  // Determine demographic dataset based on selectedFilter
  const demographicKey =
    selectedFilter === 'US Male'
      ? 'all_males_18_and_over'
      : selectedFilter === 'US Female'
        ? 'all_females_18_and_over'
        : 'all_18_and_over';

  const demographicData = reportDemographicData[demographicKey];
  if (!demographicData || !demographicData.compound_data) return false;

  // Check if the selectedCompound exists in NHANES compound data
  return Object.keys(demographicData.compound_data).some((key) => key.toLowerCase() === selectedCompound.toLowerCase());
};

const getRHData = (reportDemographicData: DemographicData | undefined) => {
  // Placeholder for RH dataset logic
  if (!reportDemographicData) return null;
};

export const truncateToTwoDecimals = (num: number) => Math.floor(num * 100) / 100;

// Filters compounds based on test name and test unit criteria.
// Excludes compounds with a test name containing "Note:" or "Disclaimer".
// Excludes compounds where the test unit does not include "ng/mL".
export const isNotesOrDisclaimer = (testName: string, testUnit: string): boolean => {
  const testNameLower = testName.toLowerCase();
  const testUnitLower = testUnit.toLowerCase();

  if (!testNameLower.includes(NOTE) && !testNameLower.includes(DISCLAIMER)) {
    return false;
  }

  if (testUnitLower.includes(CONCENTRATION_UNITS.toLowerCase())) {
    return false;
  }

  return true;
};

export function getDisplayLabel(label: string): string {
  switch (label.toLowerCase()) {
    case DETECTED.toLowerCase():
      return 'Detected';
    case BELOW_RANGE.toLowerCase():
      return 'Detected (low)';
    case ABOVE_RANGE.toLowerCase():
      return `> ${ASSUMED_ABOVE_RANGE_VALUE} ${CONCENTRATION_UNITS}`;
    default:
      return label;
  }
}
